import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { sendBroadcast } from '../../api/owner';
import * as apps from '../../api/apps';
import { useSession } from '../../state/useSession';
import type { AppListing } from '@/backend/types';
import { Button, Screen, TextField } from '../../ui';

/**
 * A broadcast reaches every confirmed subscriber and cannot be recalled, so it confirms
 * first — in a <dialog>, not window.confirm, which blocks the page and cannot be tested.
 */
export default function Broadcast() {
  const { appId = '' } = useParams();
  const { user } = useSession();
  const [app, setApp] = useState<AppListing | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    let live = true;
    void apps
      .fetchApp(appId)
      .then((a) => {
        if (live) setApp(a);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [appId]);

  async function reallySend(): Promise<void> {
    dialog.current?.close();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const out = await sendBroadcast(appId, {
        subject: subject.trim(),
        body: body.trim(),
        senderAddress: user?.email ?? '',
      });
      setResult(`Sent to ${out.sent} of ${out.recipients} subscribers.`);
      setSubject('');
      setBody('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That broadcast could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  const ready = subject.trim().length > 0 && body.trim().length > 0;

  return (
    <Screen title="Broadcast">
      <p className="type-body project-meta">
        Goes to everyone who confirmed a subscription to {app?.name ?? 'this app'}.
      </p>

      <TextField label="Subject" value={subject} onChangeText={setSubject} />
      <TextField label="Message" value={body} onChangeText={setBody} multiline rows={6} />

      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
      {result && <p role="status" className="type-body">{result}</p>}

      <Button title="Send" onPress={() => dialog.current?.showModal()} disabled={!ready} loading={busy} />

      <dialog ref={dialog} aria-label="Send to everyone" className="confirm-dialog">
        <h2 className="type-title">Send to everyone?</h2>
        <p className="type-body">
          Every confirmed subscriber gets this email. It cannot be recalled.
        </p>
        <div className="build-actions">
          <Button title="Yes, send" onPress={() => void reallySend()} />
          <Button title="Cancel" variant="ghost" onPress={() => dialog.current?.close()} />
        </div>
      </dialog>
    </Screen>
  );
}
